<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { DateTime } from 'luxon';
import { PerfusionScanModel } from '@/models/perfusionScan';
import PerfusionScanFrames from './PerfusionScanFrames.vue';

const props = defineProps({
  scan: {
    type: Object as PropType<PerfusionScanModel>,
    required: true
  },
  /** Needed to fetch the frame thumbnail previewed on the card. */
  pumpCenterId: {
    type: String,
    default: ''
  },
  perfusionId: {
    type: String,
    default: ''
  }
});

const emit = defineEmits<{
  select: [scan: PerfusionScanModel];
}>();

const DATE_TIME_24H_FORMAT = 'dd MMM yyyy, HH:mm';

/** How many readings the card previews before deferring to the detail view. */
const PREVIEW_VALUE_COUNT = 3;

const recordedAtText = computed(() =>
  DateTime.fromJSDate(props.scan.recordedAt).toFormat(DATE_TIME_24H_FORMAT)
);

const values = computed(() => props.scan.values);

const previewValues = computed(() => values.value.slice(0, PREVIEW_VALUE_COUNT));

const remainingValueCount = computed(() =>
  Math.max(values.value.length - previewValues.value.length, 0)
);

const canPreviewFrames = computed(
  () => props.scan.frames.length > 0 && !!props.pumpCenterId && !!props.perfusionId
);
</script>

<template>
  <div class="scan-card"
    role="button"
    tabindex="0"
    :aria-label="`View values collected from ${scan.machineName} at ${recordedAtText}`"
    @click="emit('select', scan)"
    @keydown.enter.prevent="emit('select', scan)"
    @keydown.space.prevent="emit('select', scan)">
    <div class="scan-header">
      <span class="scan-time">
        <FontAwesomeIcon :icon="['far', 'clock']" class="scan-time-icon"/>
        {{ recordedAtText }}
      </span>
      <span class="scan-status" :class="{ failed: scan.isFailed }">{{ scan.status }}</span>
    </div>

    <div class="scan-machine">
      <FontAwesomeIcon :icon="['far', 'monitor-waveform']" class="scan-machine-icon"/>
      {{ scan.machineName }}
    </div>

    <div class="scan-body">
      <PerfusionScanFrames v-if="canPreviewFrames"
        :scan="scan"
        :pump-center-id="pumpCenterId"
        :perfusion-id="perfusionId"
        :max-frames="1"
        :compact="true"
        :interactive="false"
        class="scan-thumb"/>

      <div class="scan-values" v-if="previewValues.length > 0">
        <div class="scan-value" v-for="value in previewValues" :key="value.path">
          <span class="scan-value-label">{{ value.label }}</span>
          <span class="scan-value-number">{{ value.value }}</span>
        </div>
        <div class="scan-value-more" v-if="remainingValueCount > 0">
          +{{ remainingValueCount }} more
        </div>
      </div>
      <div class="scan-values-empty" v-else>No values were recorded for this scan.</div>
    </div>

    <div class="scan-footer">
      <span class="scan-meta" v-if="scan.frames.length > 0">
        {{ scan.frames.length }} {{ scan.frames.length === 1 ? 'frame' : 'frames' }}
      </span>
      <span class="scan-meta" v-else>No frames</span>
      <span class="scan-open">
        View values
        <FontAwesomeIcon :icon="['far', 'chevron-right']" class="scan-open-icon"/>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "../../../assets/_variables.module.scss" as *;

.scan-card {
  background: #f8f9fc;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 12px 14px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: var(--p-primary-color, #{$primary-color});
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
    outline: none;

    .scan-open {
      opacity: 1;
    }
  }
}

.scan-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.scan-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $primary-color;
  background: rgba(0, 102, 255, 0.08);
  padding: 2px 8px;
  border-radius: 12px;

  .scan-time-icon {
    font-size: 10px;
  }
}

.scan-status {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);

  &.failed {
    color: hsl(0, 65%, 45%);
  }
}

.scan-machine {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-heading);
  word-break: break-word;

  .scan-machine-icon {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}

.scan-body {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
}

.scan-thumb {
  flex-shrink: 0;
}

.scan-values {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.scan-value {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  font-size: 12px;
}

.scan-value-label {
  color: rgba(0, 0, 0, 0.55);
  word-break: break-word;
}

.scan-value-number {
  font-weight: 600;
  color: var(--color-text);
  text-align: right;
  word-break: break-word;
}

.scan-value-more {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}

.scan-values-empty {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.scan-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--color-border);
  padding-top: 8px;
  margin-top: 10px;
}

.scan-meta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}

.scan-open {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: $primary-color;
  opacity: 0;
  transition: opacity 0.15s ease;

  .scan-open-icon {
    font-size: 9px;
  }
}
</style>
